// src/features/usuarios/UsuarioList.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axiosConfig";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaTimes,
  FaUserEdit,
  FaTrash,
  FaUserPlus,
} from "react-icons/fa";

function UsuarioList() {
  const navigate = useNavigate();

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rolSeleccionado, setRolSeleccionado] = useState("Todos");
  const [busqueda, setBusqueda] = useState("");

  const [mostrarModal, setMostrarModal] = useState(false);
  const [usuarioActual, setUsuarioActual] = useState({
    id: "",
    nombre: "",
    correo: "",
    celular: "",
    rol: "",
  });
  const [errores, setErrores] = useState({});

  const roles = ["Todos", "ADMIN", "MESERO", "CAJERO", "COCINA"];

  useEffect(() => {
    const obtenerUsuarios = async () => {
      setLoading(true);
      try {
        const res = await axios.get("/usuarios");
        setUsuarios(res.data || []);
      } catch (error) {
        console.error("Error al cargar usuarios:", error);
        toast.error("❌ Error al cargar los usuarios");
      } finally {
        setLoading(false);
      }
    };

    obtenerUsuarios();
  }, []);


  // Filtrado derivado
  const usuariosFiltrados = useMemo(() => {
    let data = [...usuarios];

    if (rolSeleccionado !== "Todos") {
      data = data.filter((u) => u.rol === rolSeleccionado);
    }

    if (busqueda.trim() !== "") {
      const q = busqueda.toLowerCase();
      data = data.filter(
        (u) =>
          u?.nombre?.toLowerCase().includes(q) ||
          (u?.celular ?? "").toString().includes(busqueda)
      );
    }

    return data;
  }, [usuarios, rolSeleccionado, busqueda]);

  // Abrir modal para editar
  const abrirModalEditar = (usuario) => {
    setUsuarioActual({ ...usuario });
    setErrores({});
    setMostrarModal(true);
  };

  // Validación
  const validarFormulario = () => {
    const nuevosErrores = {};
    if (!usuarioActual.nombre || usuarioActual.nombre.length < 3)
      nuevosErrores.nombre = "El nombre debe tener al menos 3 caracteres.";

    const correoRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!usuarioActual.correo || !correoRegex.test(usuarioActual.correo))
      nuevosErrores.correo = "Correo electrónico inválido.";

    const celularRegex = /^[0-9]{10}$/;
    if (!usuarioActual.celular || !celularRegex.test(usuarioActual.celular))
      nuevosErrores.celular = "El número de celular debe tener 10 dígitos.";

    if (!usuarioActual.rol) nuevosErrores.rol = "Debe seleccionar un rol.";

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  // Guardar cambios
  const guardarCambios = async () => {
    if (!validarFormulario()) {
      toast.warning("⚠️ Corrige los errores antes de continuar");
      return;
    }
    try {
      await axios.put(`/usuarios/${usuarioActual.id}`, usuarioActual);
      toast.success("✅ Usuario actualizado correctamente");
      setMostrarModal(false);

      setUsuarios((prev) =>
        prev.map((u) => (u.id === usuarioActual.id ? usuarioActual : u))
      );
    } catch (error) {
      console.error("Error al actualizar usuario:", error);
      toast.error("❌ No se pudo actualizar el usuario");
    }
  };

  // Eliminar usuario
  const eliminarUsuario = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar este usuario?")) return;
    try {
      await axios.delete(`/usuarios/${id}`);
      toast.success("🗑️ Usuario eliminado correctamente");
      setUsuarios((prev) => prev.filter((u) => u.id !== id));
    } catch (error) {
      toast.error("❌ No se pudo eliminar el usuario");
    }
  };

  // Badge de rol
  const getRolBadgeClasses = (rol) => {
    switch (rol) {
      case "ADMIN":
        return "bg-rose-500/15 text-rose-300 border-rose-500/60";
      case "MESERO":
        return "bg-sky-500/15 text-sky-300 border-sky-500/60";
      case "CAJERO":
        return "bg-amber-500/15 text-amber-300 border-amber-500/60";
      case "COCINA":
        return "bg-emerald-500/15 text-emerald-300 border-emerald-500/60";
      default:
        return "bg-slate-500/15 text-slate-300 border-slate-500/60";
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white/95 text-slate-900 dark:bg-slate-900/90 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6 mt-3">
        {/* Encabezado con filtros y buscador */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-amber-500 dark:text-amber-300">
            Gestión de Usuarios
          </h2>

          <div className="flex flex-wrap items-center gap-2">
            {/* Filtro de rol */}
            <select
              className="px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
              style={{ minWidth: "150px" }}
              value={rolSeleccionado}
              onChange={(e) => setRolSeleccionado(e.target.value)}
            >
              {roles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>

            {/* Buscador */}
            <div className="flex items-center rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 overflow-hidden">
              <span className="pl-2 pr-1 text-slate-400">
                <FaSearch className="w-3.5 h-3.5" />
              </span>
              <input
                type="text"
                placeholder="Buscar por nombre o celular..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="px-2 py-1.5 text-xs sm:text-sm bg-transparent text-slate-800 dark:text-slate-100 focus:outline-none"
                style={{ width: "170px" }}
              />
              {busqueda && (
                <button
                  type="button"
                  onClick={() => setBusqueda("")}
                  className="px-2 py-1.5 text-xs text-slate-400 hover:text-rose-400"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Botón registrar */}
            <button
              onClick={() => navigate("/usuarios/crear")}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 transition-colors"
            >
              <FaUserPlus />
              Registrar Usuario
            </button>
          </div>
        </div>

        {/* Tabla */}
        <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-800">
          {/* 👇 wrapper para scroll horizontal en móvil */}
          <div className="w-full overflow-x-auto">
            {/* en móvil min-w fija, en escritorio ocupa todo */}
            <table className="w-full min-w-[800px] md:min-w-full text-sm">
              <thead className="bg-slate-100 dark:bg-slate-950/80">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                    ID
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                    Nombre
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                    Correo
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Celular
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Rol
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-6">
                      <div className="flex justify-center">
                        <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    </td>
                  </tr>
                ) : usuariosFiltrados.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-4 py-4 text-center text-slate-500 dark:text-slate-400 text-sm"
                    >
                      No se encontraron usuarios
                    </td>
                  </tr>
                ) : (
                  usuariosFiltrados.map((u) => (
                    <tr
                      key={u.id}
                      className="odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/60 dark:even:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <td className="px-4 py-2 text-left align-middle">
                        {u.id}
                      </td>
                      <td className="px-4 py-2 text-left align-middle font-medium">
                        {u.nombre}
                      </td>
                      <td className="px-4 py-2 text-left align-middle">
                        {u.correo}
                      </td>
                      <td className="px-4 py-2 text-center align-middle">
                        {u.celular}
                      </td>
                      <td className="px-4 py-2 text-center align-middle">
                        <span
                          className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getRolBadgeClasses(
                            u.rol
                          )}`}
                        >
                          {u.rol}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center align-middle">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => abrirModalEditar(u)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-sky-500 text-sky-300 hover:bg-sky-500 hover:text-slate-900 transition-colors"
                          >
                            <FaUserEdit />
                            Editar
                          </button>
                          <button
                            onClick={() => eliminarUsuario(u.id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-rose-500 text-rose-300 hover:bg-rose-500 hover:text-slate-900 transition-colors"
                          >
                            <FaTrash />
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal de edición */}
      {mostrarModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:!bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:!bg-slate-800">
              <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100">
                ✏️ Editar Usuario
              </h3>
              <button
                onClick={() => setMostrarModal(false)}
                className="p-1 rounded-full border border-slate-300 dark:border-slate-600
                          !bg-slate-100 dark:!bg-slate-800
                          !text-slate-600 dark:!text-slate-200
                          hover:!bg-slate-200 dark:hover:!bg-slate-700 transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    value={usuarioActual.nombre}
                    onChange={(e) =>
                      setUsuarioActual({
                        ...usuarioActual,
                        nombre: e.target.value,
                      })
                    }
                    className={`w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-slate-900/70 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent ${
                      errores.nombre
                        ? "border-rose-500"
                        : "border-slate-300 dark:border-slate-600"
                    }`}
                  />
                  {errores.nombre && (
                    <p className="mt-1 text-xs text-rose-400">{errores.nombre}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    value={usuarioActual.correo}
                    onChange={(e) =>
                      setUsuarioActual({
                        ...usuarioActual,
                        correo: e.target.value,
                      })
                    }
                    className={`w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-slate-900/70 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent ${
                      errores.correo
                        ? "border-rose-500"
                        : "border-slate-300 dark:border-slate-600"
                    }`}
                  />
                  {errores.correo && (
                    <p className="mt-1 text-xs text-rose-400">{errores.correo}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    Número de celular
                  </label>
                  <input
                    type="text"
                    value={usuarioActual.celular}
                    onChange={(e) =>
                      setUsuarioActual({
                        ...usuarioActual,
                        celular: e.target.value,
                      })
                    }
                    className={`w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-slate-900/70 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent ${
                      errores.celular
                        ? "border-rose-500"
                        : "border-slate-300 dark:border-slate-600"
                    }`}
                  />
                  {errores.celular && (
                    <p className="mt-1 text-xs text-rose-400">{errores.celular}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    Rol
                  </label>
                  <select
                    value={usuarioActual.rol}
                    onChange={(e) =>
                      setUsuarioActual({
                        ...usuarioActual,
                        rol: e.target.value,
                      })
                    }
                    className={`w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-slate-900/70 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent ${
                      errores.rol
                        ? "border-rose-500"
                        : "border-slate-300 dark:border-slate-600"
                    }`}
                  >
                    <option value="">Seleccionar rol</option>
                    {roles
                      .filter((r) => r !== "Todos")
                      .map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                  </select>
                  {errores.rol && (
                    <p className="mt-1 text-xs text-rose-400">{errores.rol}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:!bg-slate-800 rounded-b-2xl">
              <button
                onClick={() => setMostrarModal(false)}
                className="px-4 py-2 rounded-lg text-xs sm:text-sm font-medium
                          border border-slate-300 dark:border-slate-600
                          !bg-slate-100 dark:!bg-slate-800
                          !text-slate-700 dark:!text-slate-200
                          hover:!bg-slate-200 dark:hover:!bg-slate-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={guardarCambios}
                className="px-4 py-2 rounded-lg text-xs sm:text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 transition-colors"
              >
                💾 Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UsuarioList;
